import { useEffect, useState } from 'react';
import type { MachineSelectOption } from '@/components/machines/MachineSelect';
import { apiFetch } from '@/lib/api';
import { parseMachineSelectOptions } from '@/lib/machineCatalog';

export interface UseMachineCatalogOptions {
  pageSize?: number;
  isActive?: boolean;
  /** When false, skips fetching entirely (for mode-dependent catalogs). */
  enabled?: boolean;
}

export function useMachineCatalog(options: UseMachineCatalogOptions = {}) {
  const { pageSize = 500, isActive, enabled = true } = options;
  const [machines, setMachines] = useState<MachineSelectOption[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setMachines([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ pageSize: String(pageSize) });
        if (typeof isActive === 'boolean') {
          params.set('isActive', String(isActive));
        }

        const res = await apiFetch(`/api/v1/machines?${params}`);
        if (!res.ok) {
          throw new Error('Failed to load machines');
        }
        const data = await res.json();
        if (!cancelled) {
          setMachines(parseMachineSelectOptions(data));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load machines');
          setMachines([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [pageSize, isActive, enabled]);

  return { machines, loading, error };
}
